/**
 * Template: Product
 * ------------------------------------------------------------------------------
 * Entry file for the core Product template.
 *
 */
import { waitFor } from '~/helpers/dom'
import {
  getProductTagValueByKey,
  recordProductRecentlyViewed,
} from '~/helpers/product'

import { storageNamespace } from '~/helpers/storage-namespaces'

sessionStorage.removeItem(storageNamespace.pinia.product)

/**
 * Global states.
 */
const states = {
  currentProductHandle: '',
  initialProductHandle: mint.page.handle,
  initialViewEventSent: false,
}

/**
 * Dispatch a 'View' event to Attraqt.
 * @param {String|Number} id - Shopify product id.
 * @event Attraqt#view
 */
function handleAttraqtTracking({ id = false }) {
  mint.EventBus.dispatch('attraqt:trackEvent', {
    'action': 'view',
    'target': {
      'page': location.pathname,
      'product': `${id}`,
    },
    'metadata': {
      'locale': 'en_GB',
    },
  })
}

/**
 * Dispatch a 'View' event to Bloomreach.
 * @param {Object} product - Formatted Shopify product object.
 * @param {Object} currentVariant - Formatted Shopify variant object.
 * @event Exponea#view_item
 */
function handleBloomreachTracking({ product, currentVariant }) {
  let targetVariant = currentVariant
  const productOptions = {}

  if (!targetVariant?.id) {
    targetVariant = product.variants[0]
  }

  for (const selectedOptions of targetVariant.selectedOptions) {
    const key = `product_${selectedOptions.name}`

    productOptions[key] = selectedOptions.value
  }

  /**
   * Optional tags
   * - These may not exist on every product
   * - If they exist, they can be sent to Bloomreach to improve recommendations
   */
  const optionalTagIds = [
    'colour_group',
    'end_use',
    'fabric',
    'fit',
    'grade',
    'heel_height',
    'neckline',
    'range',
    'rise',
    'sleeve_length',
    'story',
    'wearer_length',
  ]

  const optionalTags = {}

  optionalTagIds.forEach(tagId => {
    const key = `product_${tagId}`
    const value = getProductTagValueByKey(product.tags, tagId)

    if (value) {
      optionalTags[key] = value
    }
  })

  mint.EventBus.dispatch('bloomreach:trackEvent', {
    event: 'view_item',
    data: {
      product_id: product.id,
      product_variant: targetVariant.id,
      product_sku: targetVariant.sku,
      product_name: product.title,
      product_price: Number(targetVariant.price.amount / 100),
      product_category: product.collections,
      product_color: product.colour.name,
      product_currency: mint.shop.currencySymbol,
      product_flexi_fashion: product.attributes?.flexiFashionName || null,
      product_specific_type: product.types?.specific || null,
      product_type: product.types?.shopify || null,
      ...productOptions,
      ...optionalTags,
    },
  })
}

/**
 * When FlexiFashion is active, so that when this
 * URL is shared the new URL is displayed.
 * - Update the og:image meta tag
 * - Update Structured Data? JSON was invalid.
 * @param {Object} data - Data Payload.
 * @param {Object} data.product - Formatted Shopify product object.
 */
function updateMetaTags({ product }) {
  const featuredImage = product.featuredImage || false

  if (typeof featuredImage !== 'object') {
    return
  }

  const nodeSelectors = {
    metaImage: document.querySelector('meta[property="og:image"]'),
    metaImageWidth: document.querySelector('meta[property="og:image:width"]'),
    metaImageHeight: document.querySelector('meta[property="og:image:height"]'),
    structuredData: document.querySelector('script[data-mint-schema="product"]'),
  }

  /**
   * Update the og:image meta tag.
   */
  nodeSelectors.metaImage.content = featuredImage.src
  nodeSelectors.metaImageWidth.content = featuredImage.width
  nodeSelectors.metaImageHeight.content = featuredImage.height

  /**
   * Update the Structured Data JSON.
   */
  const structuredData = JSON.parse(nodeSelectors.structuredData.innerHTML)

  nodeSelectors.structuredData.innerHTML = JSON.stringify({
    ...structuredData,
    image: [`${location.protocol}${featuredImage.src}`],
  }, null, 2)
}

/**
 * DOM is ready.
 */
document.addEventListener('DOMContentLoaded', () => {

  /**
   * The product has updated.
   * - Set via Pinia, event distributed on page load and swatch tap.
   * - Only trigger events on swatch change, not variant.
   */
  mint.EventBus.on('mint:product:updated', (response) => {
    const product = response.productData
    const currentVariant = response.currentVariant

    if (!product || states.currentProductHandle === product.handle) {
      return
    }

    states.currentProductHandle = product.handle

    /**
     * Store product handle in the browser.
     * - Used for 'Recently Viewed' section.
     */
    recordProductRecentlyViewed(product)

    /**
     * Event Tracking.
     * TODO: Handle gift card.
     * - Do not run on variant update
     * - Potential for race condition.
     */
    handleAttraqtTracking({
      id: product.id,
    })

    waitFor('exponea')
      .then(() => {
        handleBloomreachTracking({
          product,
          currentVariant,
        })
      })
      .catch(() => {
        // Error handled within `waitFor`.
      })

    if (product.flexiFashion?.handle) {
      updateMetaTags({ product })
    }

    states.initialViewEventSent = true
  })
})
